import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineEnvironment, AiOutlineReload, AiOutlineUser } from 'react-icons/ai';
import { apiUrl } from '../config/api';
import { useAppSettings } from '../lib/appSettings';
import './NearbyJobs.css';

// Booked GoHighLevel jobs (today + the next 7 days), closest to where the technician is
// standing first. Location is only asked for when they tap the button — after they've
// allowed it once, it loads by itself on the next visit.
const DAYS_AHEAD = 7;
const SHOW_FIRST = 5;

const formatWhen = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === today.toDateString()) return `Today, ${time}`;
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow, ${time}`;
  return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}, ${time}`;
};

const locate = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('This device does not support location.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) =>
        reject(
          new Error(
            err.code === err.PERMISSION_DENIED
              ? 'Location is turned off for Capturify. Allow it in your browser or phone settings, then try again.'
              : 'Could not get your location. Try again in a moment.'
          )
        ),
      // Sorting by distance doesn't need a driveway-accurate fix, so take a quick one.
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 5 * 60 * 1000 }
    );
  });

const NearbyJobsSection = ({ token }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error | hidden
  const [jobs, setJobs] = useState([]);
  const [unlocated, setUnlocated] = useState(0);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const requestRef = useRef(0);

  const load = async () => {
    const requestId = ++requestRef.current;
    setStatus('loading');
    setError('');
    try {
      const { lat, lon } = await locate();
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(end.getDate() + DAYS_AHEAD);
      end.setMilliseconds(-1);
      const params = new URLSearchParams({
        lat, lon, startTime: start.getTime(), endTime: end.getTime(), limit: 20,
      });
      const response = await fetch(apiUrl(`/api/ghl/nearby-appointments?${params}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (requestId !== requestRef.current) return;
      if (!response.ok) throw new Error(data.message || data.detail || 'Could not load nearby jobs.');
      if (!data.configured) {
        setStatus('hidden');
        return;
      }
      setJobs(data.appointments || []);
      setUnlocated(data.unlocated || 0);
      setStatus('ready');
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setError(err.message);
      setStatus('error');
    }
  };

  // Already allowed location before? Load straight away instead of making them tap again.
  useEffect(() => {
    let cancelled = false;
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((p) => { if (!cancelled && p.state === 'granted') load(); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openJob = async (job) => {
    if (openingId) return;
    setOpeningId(job.id);
    try {
      const response = await fetch(apiUrl(`/api/ghl/appointments/${job.id}/open`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contactId: job.contactId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.detail || 'Could not open this job.');
      navigate(`/projects/client/${data.clientId}`);
    } catch (err) {
      setError(err.message);
      setOpeningId(null);
    }
  };

  if (status === 'hidden') return null;

  const visible = showAll ? jobs : jobs.slice(0, SHOW_FIRST);

  return (
    <div className="db-home-row nj">
      <div className="db-home-row-header nj-header">
        <h2 className="db-home-row-title">Jobs near me</h2>
        {(status === 'ready' || status === 'error') && (
          <button type="button" className="nj-refresh" onClick={load} aria-label="Refresh nearby jobs">
            <AiOutlineReload size={14} />
          </button>
        )}
      </div>

      {status === 'idle' && (
        <button type="button" className="nj-locate" onClick={load}>
          <AiOutlineEnvironment size={18} />
          Show booked jobs closest to me
        </button>
      )}

      {status === 'loading' && (
        <div className="nj-list" aria-busy="true" aria-label="Finding jobs near you">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="nj-row nj-row--skeleton db-skeleton" />)}
        </div>
      )}

      {error && <div className="db-message db-message--error nj-error">{error}</div>}

      {status === 'ready' && (
        jobs.length === 0 ? (
          <div className="db-empty">
            No booked jobs in the next {DAYS_AHEAD} days{unlocated ? ' with an address we could place on the map' : ''}.
          </div>
        ) : (
          <>
            <div className="nj-list">
              {visible.map((job) => (
                <button
                  type="button"
                  key={job.id}
                  className={`nj-row ${openingId === job.id ? 'nj-row--opening' : ''}`}
                  onClick={() => openJob(job)}
                >
                  <span className="nj-distance">
                    {job.distanceMiles < 0.1 ? 'Here' : `${job.distanceMiles} mi`}
                  </span>
                  <span className="nj-main">
                    <span className="nj-name">{job.customerName}</span>
                    <span className="nj-address">{job.address}</span>
                    <span className="nj-meta">
                      {formatWhen(job.startTime)}
                      <span className="nj-dot" aria-hidden="true">·</span>
                      <AiOutlineUser size={12} /> {job.technicianName || 'Unassigned'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            {jobs.length > SHOW_FIRST && (
              <button type="button" className="nj-more" onClick={() => setShowAll((s) => !s)}>
                {showAll ? 'Show fewer' : `Show all ${jobs.length}`}
              </button>
            )}
            {unlocated > 0 && (
              <p className="nj-note">
                {unlocated} booked job{unlocated === 1 ? '' : 's'} not shown — the address couldn&rsquo;t be found on the map.
              </p>
            )}
          </>
        )
      )}
    </div>
  );
};

// Switched on/off by the main admin (Integrations → App features). When off, nothing
// renders and the location prompt is never shown.
const NearbyJobs = (props) => {
  const { loaded, nearbyJobsEnabled } = useAppSettings({ refresh: true });
  if (!loaded || !nearbyJobsEnabled) return null;
  return <NearbyJobsSection {...props} />;
};

export default NearbyJobs;
